import React, { Fragment, useState, useEffect, useRef } from "react";
import { getCCRDataService, searchHSNCodesService } from "../../../services/CCRReportService";
import { getBRCorelationService } from "../../../services/MasterService";
import {
  Table,
  Button,
  Spin,
  Alert,
  DatePicker,
  message,
  Select,
  Input,
  AutoComplete,
} from "antd";
import { Col, Row } from "react-bootstrap";
import { Excel } from "antd-table-saveas-excel";
import Card from "../../../components/bootstrap/card";
import {
  getAllAdminIECsService,
} from "../../../services/FileDataService";
import { getTodaysDate, formatToTwoDecimals } from "../../../utilities/utils";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";

import useColumnVisibility from "../../../hooks/useColumnVisibility";
import ColumnSelector from "../../../components/common/ColumnSelector";

const { RangePicker } = DatePicker;
const CCR = () => {
  const location = useLocation();
  const [jsonData, setJsonData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summaryDashboardData, setSummaryDashboardData] = useState([]);
  const [IECOptions, setIECOptions] = useState([]);
  const userRole = localStorage.getItem("role");
  const [selectedIEC, setSelectedIEC] = useState(
    localStorage.getItem("iec_no")
  );
  const [selectedIECName, setSelectedIECName] = useState(
    localStorage.getItem("name")
  );
  const today = new Date();
  const day = String(today.getDate()).padStart(2, "0");
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const year = today.getFullYear();
  const formattedDate = `${day}-${month}-${year}`;
  const [isValid, setIsValid] = useState(true); // Validation state
  const [billNo, setBillNo] = useState("");
  const [billNoOptions, setBillNoOptions] = useState([]); // Options for dropdown
  const [selectedBillNo, setSelectedBillNo] = useState(""); // Selected bill number
  const [branchCodeNames, setBranchCodeNames] = useState([]);
  const [selectedBranchCodes, setSelectedBranchCodes] = useState([]);
  const [hsn, setHsn] = useState("");
  const [hsnOptions, setHsnOptions] = useState([]);
  const [isFetchingHsn, setIsFetchingHsn] = useState(false);
  const tradeTypes = ["IMPORT", "EXPORT"];
  const [tradeType, setTradeType] = useState("");

  const tableStyles = `
    .ccr-table .ant-table-thead > tr > th {
      white-space: normal !important;
      word-break: break-word !important;
      overflow: visible !important;
      text-overflow: unset !important;
      vertical-align: middle !important;
      line-height: 1.3 !important;
      padding-top: 8px !important;
      padding-bottom: 8px !important;
    }
    .ccr-table .ant-table-tbody > tr > td {
      white-space: nowrap;
    }
    .ccr-table .ant-table-container {
      overflow-x: auto !important;
    }
    .ccr-table .ant-table-header {
      overflow: hidden !important;
    }
    .ccr-table .ant-table-sticky-holder {
      overflow: hidden !important;
    }
  `;

  // Ref to store timeout for debouncing
  const searchTimeoutRef = useRef(null);


  const currentYear = dayjs().year();
  const defaultFrom = dayjs(`${currentYear}-01-01`);
  const defaultTo = dayjs(`${currentYear}-12-31`);
  const [dates, setDates] = useState(() => {
    // if report is opened through navigation from dashboard, then use those dates else default
    if (location.state?.passedDates?.length === 2) {
      return [
        dayjs(location.state.passedDates[0]),
        dayjs(location.state.passedDates[1]),
      ];
    }
    return [defaultFrom, defaultTo];
  });

  const columns = [
    {
      title: `HSN CODE / ${tradeType} POLICY`,
      key: "hsn",
      width: 250,
      render: (_, record) => (
        <div style={{ width: "100%", whiteSpace: "pre-wrap" }}>
          <span style={{ display: "block", fontWeight: 700 }}>
            {record.hsn}
          </span>

          <span
            style={{
              borderTop: "1px solid #555",
              display: "block",
              margin: "6px 0",
            }}
          />

          <span style={{ color: "#666", display: "block" }}>
            {`${tradeType} POLICY : `}
            <span style={{ fontWeight: "bold" }}>{record.policy}</span>
          </span>
        </div>
      ),
    },
    {
      title: tradeType ? `${tradeType}  CUSTOMS COMPLIANCE REGULATIONS (CCR)` : "CUSTOMS COMPLIANCE REGULATIONS (CCR)",
      dataIndex: "ccr",
      key: "ccr",
      width: 600,
      render: (text) => (
        <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {text || "-"}
          {tradeType === "IMPORT" && (
            <div style={{
              fontSize: "13px",
              color: "#1890ff",
              marginTop: "12px",
              borderTop: "1px solid #d9d9d9",
              paddingTop: "8px",
              fontStyle: "italic"
            }}>
              <strong>Note:</strong> Verify <strong>Bureau of Indian Standards (BIS)</strong> compliance requirements and related regulatory information directly on the official BIS website to ensure accuracy and the latest updates.
            </div>
          )}
        </div>
      ),
    },
  ];

  const { visibleKeys, setVisibleKeys, filteredColumns, resetToDefault } =
    useColumnVisibility('CCRReport', columns);

  // HSN Autocomplete Search (only triggers for 2+ characters)
  const searchHSNCodes = async (searchValue) => {
    if (!searchValue || searchValue.length < 2) {
      setHsnOptions([]);
      return;
    }

    if (!tradeType) {
      message.warning('Please select trade type first');
      return;
    }

    try {
      setIsFetchingHsn(true);
      const results = await searchHSNCodesService(searchValue, tradeType, selectedIEC);

      if (results && results.length > 0) {
        const options = results.map(item => ({
          value: item.hsn,
          label: item.hsn,
        }));
        setHsnOptions(options);
      } else {
        setHsnOptions([]);
      }
    } catch (error) {
      console.error('Error fetching HSN codes:', error);
      setHsnOptions([]);
    } finally {
      setIsFetchingHsn(false);
    }
  };

  // Debounced search (500ms delay)
  const handleHsnSearch = (value) => {
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout and store reference
    searchTimeoutRef.current = setTimeout(() => {
      searchHSNCodes(value);
    }, 800);
  };


  const fetchData = async () => {
    if (!hsn || !hsn.trim()) {
      message.error("Please Enter HSN Number");
      return;
    } else if (!tradeType) {
      message.error("Please select a trade type");
      return;
    } else if (hsn.trim().length < 6) {
      message.error("HSN must be at least 6 digits");
      return;
    }

    try {
      setIsLoading(true);
      const ccrType = tradeType === "IMPORT" ? "IMPORT_CCR" : "EXPORT_CCR";
      const hsnParam = hsn && hsn.trim() ? hsn.trim() : null;
      const response = await getCCRDataService(ccrType, hsnParam);

      setJsonData(response || []);
      setSummaryDashboardData(response || []);
      setError(null);
    } catch (error) {
      setError("Error fetching CCR data. Please try again later.");
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (userRole === 'superadmin' || userRole === 'group_head') {
      getAdminIEC();
    }
    if (userRole === "admin" || userRole === "user") {

    }
    if (selectedIEC) {
      getBRCorelationService(selectedIEC).then((response) => {
        setBranchCodeNames(response);
      });
    }
  }, []);

  useEffect(() => {
    if (selectedIEC) {
      getBRCorelationService(selectedIEC).then((response) => {
        setBranchCodeNames(response);
      });
    }
  }, [selectedIEC]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);


  const preprocessData = (data) => {
    return data.map((record) => {
      if (record.sb_date) {
      } else {
        // record.FY = '';
        record.sb_date = "";
      }
      return record;
    });
  };

  const formatDateToDDMMYY = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return "";
    }
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  const getDateOnly = (dateString) => {
    if (dateString && dateString.length !== 0) {
      const date = new Date(dateString);
      return date;
    } else {
      return "";
    }
  };

  const handleDateChange = (value) => {
    setDates(value || []);
    if (value && value.length === 2) {
      const from_date = value[0];
      const to_date = value[1];

      // Validate that both dates are selected
      if (!from_date || !to_date) {
        setIsValid(false);
        return;
      }

      // Validate that the duration is within 1 year
      const oneYearLater = from_date.add(1, "year");
      if (to_date.isAfter(oneYearLater)) {
        message.error("The selected duration should not exceed 1 year.");
        setIsValid(false);
        return;
      }

      setIsValid(true);
    } else {
      setIsValid(false);
    }
  };

  const handleSubmit = () => {
    if (!hsn || !hsn.trim()) {
      message.error("Please Enter HSN Number");
      return;
    }
    else if (tradeType === "") {
      message.error("Please Select a trade type");
      return;
    } else if (hsn.trim().length < 6) {
      message.error("HSN must be at least 6 digits");
      return;
    }
    fetchData();
  };

  const getAdminIEC = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await getAllAdminIECsService(token);

      if (response?.data) {
        const options = response.data.map((item, index) => ({
          value: `${item.iec_no};${item.fullname}`,
          label: `${item.iec_no} (${item.fullname})`,
          key: index,
        }));
        setIECOptions(options);
      }
    } catch (err) {
      message.error("Failed to load IEC list");
    }
  };

  const handleIECSelection = (val) => {
    if (val) {
      const [iec_no, iec_name] = val.split(";");
      setSelectedIEC(iec_no);
      setSelectedIECName(iec_name);
    } else {
      setSelectedIEC(null);
      setSelectedIECName("");
    }
  };

  const handleBillNoSelection = (val) => {
    if (val) {
      setSelectedBillNo(val);
      setBillNo(val);
    } else {
      setSelectedBillNo(""); //Reset selected value
      setBillNo("");
    }
  };

  const handleBranchCodeSelection = (values) => {
    setSelectedBranchCodes(values);
  };

  const handleTradeTypeSelection = (values) => {
    setTradeType(values);
  };

  const exportToExcel = async () => {
    if (!jsonData || jsonData.length === 0) {
      message.error("No data to export");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("CCR Data");

    // Define columns
    worksheet.columns = [
      { header: `HSN CODE / ${tradeType} Policy`, key: "hsn", width: 30 },
      { header: `${tradeType} CCR`, key: "ccr", width: 100 },
    ];

    // Style the header (Row 1)
    const headerRow = worksheet.getRow(1);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFF00" }, // Yellow
      };
      cell.alignment = { vertical: "middle", horizontal: "center" };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    let currentRowIndex = 2; // Start adding data from row 2

    jsonData.forEach((record) => {
      // Row A: HSN and CCR
      const row1 = worksheet.getRow(currentRowIndex);
      row1.getCell("hsn").value = record.hsn || "";
      row1.getCell("ccr").value = record.ccr || "";

      // Row B: Policy (CCR is merged later)
      const row2 = worksheet.getRow(currentRowIndex + 1);
      row2.getCell("hsn").value = record.policy || "";
      // No value for ccr in row 2 as it will be merged

      // Styling: HSN (Bold, Centered)
      row1.getCell("hsn").font = { bold: true };
      row1.getCell("hsn").alignment = {
        vertical: "middle",
        horizontal: "center",
      };

      // Styling: Policy (Normal, Centered)
      row2.getCell("hsn").alignment = {
        vertical: "middle",
        horizontal: "center",
      };

      // Styling: CCR (Merged, Top Aligned, Wrapped)
      // Merge Column 2 (CCR) across the two rows
      worksheet.mergeCells(`B${currentRowIndex}:B${currentRowIndex + 1}`);
      const ccrCell = row1.getCell("ccr");
      ccrCell.alignment = {
        wrapText: true,
        vertical: "top",
        horizontal: "left",
      };

      // Apply borders to all modified cells
      [row1, row2].forEach((row) => {
        row.getCell("hsn").border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        row.getCell("ccr").border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      currentRowIndex += 2;
    });

    // Generate Excel file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, `CCR_Report_${tradeType}_${formattedDate}.xlsx`);
  };

  return (
    <Fragment>
      <style>{tableStyles}</style>
      <Row>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between">
              <div className="header-title">
                <h4 className="card-title">
                  Customs Compliance Regulations (CCR) - {tradeType || ""} as on{" "}
                  {formattedDate}
                </h4>
              </div>
              <div className="d-flex align-items-center gap-2">
                <ColumnSelector
                  allColumns={columns}
                  visibleKeys={visibleKeys}
                  setVisibleKeys={setVisibleKeys}
                  onReset={resetToDefault}
                />
                <button
                  className="btn btn-primary cip-btn-submit"
                  disabled={isLoading || error || jsonData.length === 0}
                  onClick={exportToExcel}
                >
                  Export to Excel
                </button>
              </div>
            </Card.Header>
            <Card.Body className="d-flex justify-content-start align-items-center gap-2 ps-4">
              {(userRole === 'superadmin' || userRole === 'group_head') && (
                <Select
                  showSearch
                  placeholder="Select IEC"
                  onChange={handleIECSelection}
                  allowClear
                  options={IECOptions}
                  style={{ width: "300px" }}
                />
              )}

              <Select
                mode="single"
                allowClear
                style={{ width: 250 }}
                placeholder="Select Trade Type"
                onChange={handleTradeTypeSelection}
                options={tradeTypes.map((item) => ({
                  value: item,
                  label: `${item}`,
                }))}
              />

              <AutoComplete
                placeholder="Enter HSN Number"
                value={hsn}
                onChange={(value) => setHsn(value)}
                onSearch={handleHsnSearch}
                options={hsnOptions}
                style={{ width: "250px" }}
                notFoundContent={isFetchingHsn ? <Spin size="small" /> : 'No results found'}
                filterOption={false}
                disabled={!tradeType}
              />

              <Button
                type="primary"
                className="cip-btn-submit"
                onClick={handleSubmit}
              >
                Submit
              </Button>
            </Card.Body>
            <Card.Body className="px-0 report-page">
              {error && (
                <div className="text-center my-4">
                  <Alert
                    message="Error"
                    description={error}
                    type="error"
                    showIcon
                  />
                </div>
              )}
              <Table
                className="ccr-table"
                bordered
                columns={filteredColumns}
                dataSource={jsonData}
                loading={isLoading}
                pagination={{pageSize: 50, showTotal: (total) => `Total ${total} records`, showSizeChanger: true}}
                scroll={{ x: 'max-content' }}
                
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
};

export default CCR;
